import React from 'react';
import { isNonMemberGuestMembershipType } from '../utils/membership';
import AddGuestForm from './AddGuestForm';
import Segmented from './ui/Segmented';

/* Either field marks a guest — the server sets both, but an older record may
   carry only one, and the club's member limit reads either. */
const isGuest = u => isNonMemberGuestMembershipType(u.membershipType)
  || String(u.playerType || '').trim().toLowerCase() === 'non-member guest';

const KIND_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'members', label: 'Members' },
  { value: 'guests', label: 'Guests' },
];

function ClubPlayersView({ openNewProfile, openProfile, playerProfile, playerSearch, profileContent, setPlayerProfile, setPlayerSearch, setUsers, setView, showToast, users }) {
  const [addingGuest, setAddingGuest] = React.useState(false);
  /* Remembered for the session, so coming back from a profile does not put
     the guests back into a list somebody had just filtered them out of. */
  const [kind, setKind] = React.useState(() => {
    try { return sessionStorage.getItem('gs-players-kind') || 'all'; } catch (e) { return 'all'; }
  });
  const chooseKind = (value) => {
    setKind(value);
    try { sessionStorage.setItem('gs-players-kind', value); } catch (e) { /* private mode */ }
  };

  const guestCount = users.filter(isGuest).length;
  const shown = users.filter(u => {
    if (kind === 'members' && isGuest(u)) return false;
    if (kind === 'guests' && !isGuest(u)) return false;
    if (!playerSearch.trim()) return true;
    const q = playerSearch.toLowerCase();
    return u.name.toLowerCase().includes(q) || (u.nickname && u.nickname.toLowerCase().includes(q));
  });

  const guestAdded = (created) => {
    setUsers(prev => [...prev, created]);
    setAddingGuest(false);
    showToast && showToast(`${created.name} added as a guest player`);
    openProfile(created.id);
  };

  return (
    <div className="page-container-lg">
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '16px 0' }}>
        <button className="w3-button w3-theme-l4 w3-round" title="Back to dashboard" aria-label="Back to dashboard" onClick={() => { setView('dashboard'); setPlayerProfile(null); }}>
          <i className="fa fa-arrow-left"></i>
        </button>
        <h2 style={{ margin: 0, flex: 1 }}><i className="fa fa-users w3-text-theme w3-margin-right"></i>Club Players ({users.length})</h2>
        <button className="w3-button w3-round w3-theme-l4" aria-expanded={addingGuest}
          title="Add a guest player" onClick={() => setAddingGuest(v => !v)}>
          <i className={`fa ${addingGuest ? 'fa-times' : 'fa-user-plus'} w3-margin-right`}></i>
          <span className="w3-hide-small">{addingGuest ? 'Cancel' : 'Add Guest'}</span>
        </button>
        <button className="w3-button w3-round w3-theme-l2" onClick={openNewProfile}>
          <i className="fa fa-plus w3-margin-right"></i><span className="w3-hide-small">Add Player</span>
        </button>
      </div>
      {addingGuest && (
        <div className="w3-card w3-round w3-white w3-padding" style={{ marginBottom: '16px' }}>
          <AddGuestForm users={users} onAdded={guestAdded} onCancel={() => setAddingGuest(false)} />
        </div>
      )}
      <div className="split-pane">
        {/* LEFT — Player list */}
        <div className="split-list">
          <div className="w3-card w3-round w3-white">
            <div className="w3-container w3-padding">
              <div style={{ position: 'relative', marginBottom: '10px' }}>
                <i className="fa fa-search" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#999' }}></i>
                <input className="w3-input w3-border w3-round" placeholder="Search players…"
                  style={{ paddingLeft: '32px' }}
                  value={playerSearch} onChange={e => setPlayerSearch(e.target.value)} />
              </div>
              <Segmented ariaLabel="Show members or guests" options={KIND_OPTIONS.map(o => ({
                ...o,
                label: o.value === 'guests' ? `Guests (${guestCount})` : o.value === 'members' ? `Members (${users.length - guestCount})` : o.label,
              }))}
                value={kind} onChange={chooseKind} style={{ marginBottom: '10px' }} />
              {users.length === 0 ? (
                <p className="w3-text-grey w3-center" style={{ padding: '16px 0' }}>
                  <i className="fa fa-users w3-xxlarge w3-text-light-grey" style={{ display: 'block', marginBottom: '8px' }}></i>
                  <em>No players yet. Click "Add Player" to get started.</em>
                </p>
              ) : (
                <div className="split-list-scroll">
                  {shown.length === 0 && (
                    <p className="w3-text-grey w3-center" style={{ padding: '16px 0' }}>
                      <em>{kind === 'guests' ? 'No guest players match.' : 'No players match.'}</em>
                    </p>
                  )}
                  {shown.map(u => (
                    <div key={u.id}
                      className={`player-row w3-hover-light-grey ${playerProfile && playerProfile.id === u.id ? 'split-active' : ''}`}
                      onClick={() => openProfile(u.id)} style={{ cursor: 'pointer' }}>
                      <div className="player-avatar"><i className="fa fa-user"></i></div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 'var(--gs-text-sm)' }}>
                          {u.name}
                          {isGuest(u) && (
                            <span className="w3-tag w3-round w3-theme-l4" style={{ marginLeft: '6px', fontSize: 'var(--gs-text-xs)', padding: '1px 6px', fontWeight: 400 }}>Guest</span>
                          )}
                        </div>
                      </div>
                      <i className="fa fa-chevron-right w3-text-light-grey"></i>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* RIGHT — Player profile detail */}
        <div className="split-detail">
          {playerProfile ? profileContent : (
            <div className="w3-card w3-round w3-white" style={{ padding: '48px 24px', textAlign: 'center' }}>
              <i className="fa fa-user" style={{ fontSize: '3em', color: '#ccc' }}></i>
              <p className="w3-text-grey" style={{ marginTop: '12px' }}><em>Select a player to view their profile</em></p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ClubPlayersView;
